// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import "./LogInsights.scss";

import { 
    collection, 
    query, 
    orderBy, 
    limit, 
    onSnapshot, 
    where, 
    Timestamp,
    startAfter,
    getDocs,
    getCountFromServer
} from "firebase/firestore";
import { AuthContext } from '../../context/AuthContext';
import { firestore } from "../../context/FirebaseConfig";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { format, subMonths } from "date-fns";

import Navbar from '../../components/Navbar/Navbar';
import Sidebar from '../../components/Navbar/Sidebar';
import Dropdown from "../../components/Filters/Dropdown";
import MultiSelectDropdown from "../../components/Filters/MultiSelectDropdown";
import TimeframeFilter from "../../components/Filters/TimeframeFilter";
import UserEventsTable from "./UserEventsTable";
import EventUsersTable from "./EventUsersTable";
import EventsOverTime from "./EventsOverTime";
import SourceDistribution from "./SourceDistribution";
import SessionAnalytics from "./SessionAnalytics";
import EventCountAnalytics from "./EventCountAnalytics";
import ContentTypeAnalytics from "./ContentTypeAnalytics";
import PageViewAnalytics from "./PageViewAnalytics";

export default function LogInsights() {
    const { i18n, t } = useTranslation();
    const navigate = useNavigate();
    const { currentUser } = useContext(AuthContext);

    // State for tracking logs and users
    const [eventLogs, setEventLogs] = useState([]);
    const [eventLogCount, setEventLogCount] = useState(0);
    const [users, setUsers] = useState({});
    const [timeframe, setTimeframe] = useState({
        startDate: subMonths(new Date(), 1), 
        endDate: new Date(),
        label: "timeframe_last_month"
    });
    const [selectedUser, setSelectedUser] = useState({ id: 0, name: "All" });
    const [selectedSource, setSelectedSource] = useState({ id: 0, name: "All" });
    const [selectedEvent, setSelectedEvent] = useState({ id: 0, name: "All" });
    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    
    // State for tracking companies
    const [companies, setCompanies] = useState({});
    
    // Batch size for pagination
    const LOGS_BATCH_SIZE = 500;

    // Unsubscribe refs
    const unsubscribeUsers = useRef(null);
    const unsubscribeCompanies = useRef(null);
    const lastDocRef = useRef(null);

    // Derived state
    const sources = useMemo(() => {
        const sourceSet = new Set(eventLogs.map(log => log.source));
        return [{ id: 0, name: "All" }, ...Array.from(sourceSet).map(source => ({ id: source, name: source }))];
    }, [eventLogs]);

    const eventNames = useMemo(() => {
        const eventSet = new Set(eventLogs.map(log => log.event_name));
        return [{ id: 0, name: "All" }, ...Array.from(eventSet).map(event => ({ id: event, name: event }))];
    }, [eventLogs]);

    // Filtered logs
    const filteredLogs = useMemo(() => {
        return eventLogs.filter(log => {
            const userMatch = selectedUser.id === 0 || log.user === selectedUser.id;
            const sourceMatch = selectedSource.id === 0 || log.source === selectedSource.id;
            const eventMatch = selectedEvent.id === 0 || log.event_name === selectedEvent.id;
            
            // Handle multi-select companies
            const companyMatch = selectedCompanies.length === 0 
                ? true 
                : selectedCompanies.some(company => company.id === log.client);
                
            return userMatch && sourceMatch && eventMatch && companyMatch;
        });
    }, [eventLogs, selectedUser, selectedSource, selectedEvent, selectedCompanies]);

    // Check admin access
    useEffect(() => {
        if (!currentUser || !currentUser.info || !currentUser.info.user_flags || !currentUser.info.user_flags.includes('admin')) {
            navigate("/");
        }
    }, [currentUser, navigate]);

    // Get event log count
    async function getEventLogCount() {
        try {
            const startTimestamp = Timestamp.fromDate(timeframe.startDate);
            const endTimestamp = Timestamp.fromDate(timeframe.endDate);
            
            const countQuery = query(
                collection(firestore, "event_logs"),
                where("timestamp", ">=", startTimestamp),
                where("timestamp", "<=", endTimestamp)
            );
            
            const countSnapshot = await getCountFromServer(countQuery);
            const count = countSnapshot.data().count;
            setEventLogCount(count);
            
            return count;
        } catch (error) {
            console.error("Error getting event log count:", error);
            return 0;
        }
    }

    // Fetch initial event logs
    async function fetchInitialEventLogs() {
        setIsLoading(true);
        try {
            setEventLogs([]);
            lastDocRef.current = null;
            
            const startTimestamp = Timestamp.fromDate(timeframe.startDate);
            const endTimestamp = Timestamp.fromDate(timeframe.endDate);
            
            const q = query(
                collection(firestore, "event_logs"),
                where("timestamp", ">=", startTimestamp),
                where("timestamp", "<=", endTimestamp),
                orderBy("timestamp", "desc"),
                limit(LOGS_BATCH_SIZE)
            );
            
            const querySnapshot = await getDocs(q);
            const logs = [];
            
            querySnapshot.forEach((doc) => {
                logs.push({
                    id: doc.id,
                    ...doc.data(),
                });
            });
            
            setEventLogs(logs);
            setHasMore(!querySnapshot.empty && querySnapshot.docs.length === LOGS_BATCH_SIZE);
            
            // Save last document for pagination
            if (!querySnapshot.empty) {
                lastDocRef.current = querySnapshot.docs[querySnapshot.docs.length - 1];
            }
        } catch (error) {
            console.error("Error fetching event logs:", error);
        } finally {
            setIsLoading(false);
        }
    }

    // Load more event logs for pagination
    async function loadMoreEventLogs() {
        if (!hasMore || isLoading || !lastDocRef.current) return;
        
        setIsLoading(true);
        try {
            const startTimestamp = Timestamp.fromDate(timeframe.startDate);
            const endTimestamp = Timestamp.fromDate(timeframe.endDate);
            
            const q = query(
                collection(firestore, "event_logs"),
                where("timestamp", ">=", startTimestamp),
                where("timestamp", "<=", endTimestamp),
                orderBy("timestamp", "desc"),
                startAfter(lastDocRef.current),
                limit(LOGS_BATCH_SIZE)
            );
            
            const querySnapshot = await getDocs(q);
            const newLogs = [];
            
            querySnapshot.forEach((doc) => {
                newLogs.push({
                    id: doc.id,
                    ...doc.data(),
                });
            });
            
            setEventLogs(prevLogs => [...prevLogs, ...newLogs]);
            setHasMore(!querySnapshot.empty && querySnapshot.docs.length === LOGS_BATCH_SIZE);
            
            // Update last document for next pagination
            if (!querySnapshot.empty) {
                lastDocRef.current = querySnapshot.docs[querySnapshot.docs.length - 1];
            }
        } catch (error) {
            console.error("Error loading more event logs:", error);
        } finally {
            setIsLoading(false);
        }
    }

    // Load all remaining event logs
    async function loadAllEventLogs() {
        if (!hasMore || isLoading || !lastDocRef.current) return;
        
        setIsLoading(true);
        try {
            let keepLoading = true;
            let totalLoaded = 0;
            
            while (keepLoading && lastDocRef.current) {
                const startTimestamp = Timestamp.fromDate(timeframe.startDate);
                const endTimestamp = Timestamp.fromDate(timeframe.endDate);
                
                const q = query(
                    collection(firestore, "event_logs"),
                    where("timestamp", ">=", startTimestamp),
                    where("timestamp", "<=", endTimestamp),
                    orderBy("timestamp", "desc"),
                    startAfter(lastDocRef.current),
                    limit(LOGS_BATCH_SIZE)
                );
                
                const querySnapshot = await getDocs(q);
                const newLogs = [];
                
                querySnapshot.forEach((doc) => {
                    newLogs.push({
                        id: doc.id,
                        ...doc.data(),
                    });
                });
                
                if (newLogs.length > 0) {
                    setEventLogs(prevLogs => [...prevLogs, ...newLogs]);
                    totalLoaded += newLogs.length;
                    
                    // Update last document for next pagination
                    lastDocRef.current = querySnapshot.docs[querySnapshot.docs.length - 1];
                    
                    // Check if we should continue loading
                    keepLoading = !querySnapshot.empty && querySnapshot.docs.length === LOGS_BATCH_SIZE;
                } else {
                    keepLoading = false;
                }
            }
            
            setHasMore(false);
            console.log(`Loaded all remaining logs. Total new logs: ${totalLoaded}`);
        } catch (error) {
            console.error("Error loading all event logs:", error);
        } finally {
            setIsLoading(false);
        }
    }

    // Fetch users
    async function getUsers() {
        if (unsubscribeUsers.current) unsubscribeUsers.current();

        const q = query(collection(firestore, "users"));
        const unsub = onSnapshot(q, (querySnapshot) => {
            const usersNew = {};
            querySnapshot.forEach((doc) => {
                usersNew[doc.id] = doc.data();
            });
            setUsers(usersNew);
        });

        unsubscribeUsers.current = () => unsub();
    }
    
    // Fetch companies
    async function getCompanies() {
        if (unsubscribeCompanies.current) unsubscribeCompanies.current();

        const q = query(collection(firestore, "clients"));
        const unsub = onSnapshot(q, (querySnapshot) => {
            const companiesNew = {};
            querySnapshot.forEach((doc) => {
                companiesNew[doc.id] = doc.data();
            });
            setCompanies(companiesNew);
        });

        unsubscribeCompanies.current = () => unsub();
    }

    // Effect hooks for data fetching
    useEffect(() => {
        getUsers();
        getCompanies();
        return () => {
            if (unsubscribeUsers.current) unsubscribeUsers.current();
            if (unsubscribeCompanies.current) unsubscribeCompanies.current();
        };
    }, []);

    useEffect(() => {
        getEventLogCount();
        fetchInitialEventLogs();
    }, [timeframe]);

    // Get user display names for logs
    const getUserDisplayName = (userId) => {
        return users[userId]?.displayName || userId;
    };
    
    // Get company display names for logs
    const getCompanyDisplayName = (companyId) => {
        return companies[companyId]?.company_displayname || companyId;
    };

    // Create user options for dropdown
    const userOptions = useMemo(() => {
        const uniqueUserIds = [...new Set(eventLogs.map(log => log.user))];
        return [
            { id: 0, name: "All" },
            ...uniqueUserIds.map(id => ({
                id,
                name: getUserDisplayName(id)
            }))
        ];
    }, [eventLogs, users]);
    
    // Create company options for dropdown
    const companyOptions = useMemo(() => {
        const uniqueCompanyIds = [...new Set(eventLogs.map(log => log.client).filter(Boolean))];
        return [
            { id: 0, name: "All" },
            ...uniqueCompanyIds.map(id => ({
                id,
                name: getCompanyDisplayName(id)
            }))
        ];
    }, [eventLogs, companies]);

    // Determine if we should show dashboard content
    const showDashboardContent = !isLoading && filteredLogs.length > 0;

    return (
        <div className="log-insights">
            <Sidebar />
            <div className="log-insights--right">
                <Navbar 
                    title={t("sidebar_log_insights") || "Log Insights"}
                />
                <div className="filters-container">
                    <div className="filters-row">
                        <MultiSelectDropdown 
                            options={companyOptions}
                            setSelectedOptions={setSelectedCompanies}
                            defaultOptions={[]}
                            title={t("log_insights_company") || "Company"}
                            searchPlaceholder="search_companies"
                            noResultsTranslationKey="no_companies_found"
                        />
                        <Dropdown 
                            options={userOptions}
                            setOption={setSelectedUser}
                            defaultOption={{ id: 0, name: "All" }}
                            title={t("log_insights_user") || "User"}
                        />
                        <Dropdown 
                            options={sources}
                            setOption={setSelectedSource}
                            defaultOption={{ id: 0, name: "All" }}
                            title={t("log_insights_source") || "Source"}
                        />
                        <Dropdown 
                            options={eventNames}
                            setOption={setSelectedEvent}
                            defaultOption={{ id: 0, name: "All" }}
                            title={t("log_insights_event") || "Event"}  
                        />
                        <TimeframeFilter 
                            timeframe={timeframe}
                            setTimeframe={setTimeframe}
                        />
                    </div>
                    <div className="log-count-info">
                        <span className="log-count">
                            {t('log_insights_showing_logs') || 'Showing'}: {eventLogs.length} / {eventLogCount} {t('log_insights_logs') || 'logs'}
                        </span>
                        {hasMore && (
                            <div className="buttons-container">
                                <button 
                                    className="load-more-button" 
                                    onClick={loadMoreEventLogs}
                                    disabled={isLoading}
                                >
                                    {isLoading ? 
                                        (t('loading') || 'Loading...') : 
                                        (t('log_insights_load_more') || 'Load More')}
                                </button>
                                <button 
                                    className="load-all-button" 
                                    onClick={loadAllEventLogs}
                                    disabled={isLoading}
                                >
                                    {isLoading ? 
                                        (t('loading') || 'Loading...') : 
                                        (t('log_insights_load_all') || 'Load All')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {showDashboardContent ? (
                    <div className="dashboard-container">
                        {/* First row with standard charts */}
                        <div className="dashboard-row">
                            <div className="dashboard-card">
                                <EventsOverTime logs={filteredLogs} timeframe={timeframe} />
                            </div>
                            <div className="dashboard-card">
                                <SourceDistribution logs={filteredLogs} />
                            </div>
                        </div>

                        {/* Second row with session and event count analytics */}
                        <div className="dashboard-row">
                            <div className="dashboard-card">
                                <PageViewAnalytics 
                                    logs={filteredLogs} 
                                    users={users} 
                                    getUserDisplayName={getUserDisplayName}
                                    companies={companies} 
                                    getCompanyDisplayName={getCompanyDisplayName} 
                                />
                            </div>
                            <div className="dashboard-card">
                                <ContentTypeAnalytics 
                                    logs={filteredLogs} 
                                    users={users} 
                                    getUserDisplayName={getUserDisplayName}
                                    companies={companies} 
                                    getCompanyDisplayName={getCompanyDisplayName} 
                                />
                            </div>
                            
                        </div>

                        {/* Third row with content type and page view analytics */}
                        <div className="dashboard-row">
                            <div className="dashboard-card">
                                <EventCountAnalytics 
                                    logs={filteredLogs} 
                                    t={t} 
                                    companies={companies} 
                                    getCompanyDisplayName={getCompanyDisplayName} 
                                />
                            </div>
                            <div className="dashboard-card">
                                <SessionAnalytics 
                                    logs={filteredLogs} 
                                    t={t} 
                                    companies={companies} 
                                    getCompanyDisplayName={getCompanyDisplayName} 
                                />
                            </div>
                        </div>
                        

                        {/* Fourth row with tables */}
                        <div className="dashboard-row">
                            <div className="dashboard-full-width">
                                <UserEventsTable 
                                    logs={filteredLogs} 
                                    users={users} 
                                    getUserDisplayName={getUserDisplayName} 
                                />
                            </div>
                        </div>

                        <div className="dashboard-row">
                            <div className="dashboard-full-width">
                                <EventUsersTable 
                                    logs={filteredLogs}
                                    users={users}
                                    getUserDisplayName={getUserDisplayName}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="no-logs-message">
                        {isLoading ? (
                            <div className="loading-container">
                                <div className="spinner"></div>
                                <p>{t('loading') || 'Loading...'}</p>
                            </div>
                        ) : (
                            <p>{t('no_logs_matching_filters') || 'No logs matching the selected filters.'}</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
} 